import { Link } from "@tanstack/react-router";

import { SLUG_TO_ICON } from "@/components/game-categories";
import type { FacetItemType } from "@/components/pagination";
import { cn } from "@/lib/utils";

function ClearIcon({ className, ...props }: React.ComponentProps<"svg">) {
  return (
    <svg
      aria-hidden="true"
      className={cn("size-4 text-rose-red", className)}
      viewBox="0 0 16 16"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <path
        clipRule="evenodd"
        d="M10.474 4.47a.75.75 0 111.06 1.06L9.063 8.002l2.471 2.471a.75.75 0 01-1.06 1.061L8.001 9.063 5.53 11.534a.75.75 0 01-1.06-1.06l2.47-2.472L4.47 5.53a.75.75 0 011.06-1.06l2.472 2.47 2.472-2.471z"
        fill="currentColor"
        fillRule="evenodd"
      />
    </svg>
  );
}

interface SubcategoriesFilterItemProps {
  facet: FacetItemType;
  isActive: boolean;
  urlPrefix: string;
}

function SubcategoriesFilterItem({
  facet,
  isActive,
  urlPrefix,
}: SubcategoriesFilterItemProps) {
  const Icon = SLUG_TO_ICON[facet.slug];
  const color = isActive ? "text-rose-red" : "text-slate-gray";

  return (
    <li
      className={cn(
        "relative flex h-7.5 items-center gap-1.5 rounded-[15px] px-2.5 transition-colors",
        isActive ? "bg-white" : "bg-eerie-black"
      )}
    >
      {Icon && <Icon className={cn("size-4 transition-colors", color)} />}
      <Link
        className={cn(
          "link-overlay whitespace-nowrap font-medium text-14 transition-colors",
          color
        )}
        replace
        to={isActive ? urlPrefix : `${urlPrefix}/${facet.slug}`}
      >
        {facet.name} ({facet.count})
      </Link>
      {isActive && <ClearIcon />}
    </li>
  );
}

interface SubcategoriesFilterProps {
  facets: FacetItemType[];
  selectedCategory?: string | string[];
  urlPrefix: string;
}

export default function SubcategoriesFilter({
  facets,
  selectedCategory,
  urlPrefix,
}: SubcategoriesFilterProps) {
  return (
    <div className="scrollbar-hidden -mx-[25px] w-screen overflow-auto md:mx-0 md:w-auto">
      <ul className="flex gap-2.5 px-[25px] pb-5 md:px-0">
        {facets.map((facet) => (
          <SubcategoriesFilterItem
            facet={facet}
            isActive={selectedCategory === facet.slug}
            key={facet.id}
            urlPrefix={urlPrefix}
          />
        ))}
      </ul>
    </div>
  );
}
